import { EmailBlock, EmailDesign } from '../types';

/**
 * Serialize EmailDesign to JSON string for storage
 */
export function serializeDesign(design: EmailDesign): string {
  return JSON.stringify(design, null, 2);
}

/**
 * Deserialize JSON string to EmailDesign
 */
export function deserializeDesign(json: string): EmailDesign | null {
  try {
    return JSON.parse(json) as EmailDesign;
  } catch (error) {
    console.error('Failed to deserialize email design:', error);
    return null;
  }
}

/**
 * Create a new block with default content based on type
 */
export function createBlock(type: EmailBlock['type'], id?: string): EmailBlock {
  const blockId = id || `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const defaultBlocks: Record<EmailBlock['type'], EmailBlock> = {
    text: {
      id: blockId,
      type: 'text',
      content: {
        text: 'Enter your text here',
        fontSize: 16,
        color: '#000000',
        textAlign: 'left',
        padding: '10px'
      }
    },
    image: {
      id: blockId,
      type: 'image',
      content: {
        src: '',
        alt: '',
        width: '100%',
        align: 'center'
      }
    },
    button: {
      id: blockId,
      type: 'button',
      content: {
        text: 'Click here',
        link: '#',
        backgroundColor: '#3b82f6',
        textColor: '#ffffff',
        fontSize: 16,
        padding: '12px 24px',
        borderRadius: '4px',
        align: 'center'
      }
    },
    divider: {
      id: blockId,
      type: 'divider',
      content: {
        color: '#e5e7eb',
        thickness: 1,
        style: 'solid',
        spacing: '20px',
        width: '100%',
        align: 'center'
      }
    },
    spacer: {
      id: blockId,
      type: 'spacer',
      content: {
        height: 20
      }
    },
    social: {
      id: blockId,
      type: 'social',
      content: {
        platforms: [],
        iconSize: 32,
        iconColor: '#000000',
        spacing: 10,
        align: 'center'
      }
    },
    html: {
      id: blockId,
      type: 'html',
      content: {
        html: '<p>Custom HTML content</p>'
      }
    },
    row: {
      id: blockId,
      type: 'row',
      content: {
        backgroundColor: 'transparent',
        padding: '0',
        columns: 1,
        align: 'center'
      },
      children: []
    },
    column: {
      id: blockId,
      type: 'column',
      content: {
        backgroundColor: 'transparent',
        padding: '10px',
        align: 'left'
      },
      children: []
    },
    header: {
      id: blockId,
      type: 'header',
      content: {
        backgroundColor: '#ffffff',
        padding: '20px',
        logo: {
          src: '',
          alt: 'Logo',
          width: '150px'
        },
        navigation: []
      }
    },
    footer: {
      id: blockId,
      type: 'footer',
      content: {
        backgroundColor: '#f3f4f6',
        padding: '30px 20px',
        textColor: '#6b7280',
        fontSize: 12,
        showUnsubscribeLink: true,
        showPreferenceCenter: true,
        unsubscribeText: 'Unsubscribe',
        preferenceCenterText: 'Manage Preferences',
        companyInfo: '',
        socialLinks: [],
        copyrightText: '© 2024 Company Name. All rights reserved.'
      }
    }
  };

  return { ...defaultBlocks[type] };
}

/**
 * Clone a block with a new ID
 */
export function cloneBlock(block: EmailBlock): EmailBlock {
  const newId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  return {
    ...block,
    id: newId,
    children: block.children ? block.children.map(cloneBlock) : undefined
  };
}

/**
 * Find a block by ID in the design tree
 */
export function findBlock(design: EmailDesign, blockId: string): EmailBlock | null {
  function search(blocks: EmailBlock[]): EmailBlock | null {
    for (const block of blocks) {
      if (block.id === blockId) {
        return block;
      }
      if (block.children) {
        const found = search(block.children);
        if (found) return found;
      }
    }
    return null;
  }
  return search(design.blocks);
}

/**
 * Remove a block by ID from the design
 */
export function removeBlock(design: EmailDesign, blockId: string): EmailDesign {
  function removeFromBlocks(blocks: EmailBlock[]): EmailBlock[] {
    return blocks
      .filter(block => block.id !== blockId)
      .map(block => ({
        ...block,
        children: block.children ? removeFromBlocks(block.children) : undefined
      }));
  }

  return {
    ...design,
    blocks: removeFromBlocks(design.blocks)
  };
}

/**
 * Update a block in the design
 */
export function updateBlock(design: EmailDesign, blockId: string, updates: Partial<EmailBlock>): EmailDesign {
  function updateInBlocks(blocks: EmailBlock[]): EmailBlock[] {
    return blocks.map(block => {
      if (block.id === blockId) {
        return { ...block, ...updates };
      }
      if (block.children) {
        return {
          ...block,
          children: updateInBlocks(block.children)
        };
      }
      return block;
    });
  }

  return {
    ...design,
    blocks: updateInBlocks(design.blocks)
  };
}

/**
 * Move a block to a new position
 */
export function moveBlock(design: EmailDesign, blockId: string, newIndex: number, parentId?: string): EmailDesign {
  // Find and remove the block
  const blockToMove = findBlock(design, blockId);
  if (!blockToMove) return design;

  let designWithoutBlock = removeBlock(design, blockId);

  // Insert at new position
  function insertAt(blocks: EmailBlock[], targetIndex: number): EmailBlock[] {
    const newBlocks = [...blocks];
    newBlocks.splice(targetIndex, 0, blockToMove!);
    return newBlocks;
  }

  const updateParent = (blocks: EmailBlock[]): EmailBlock[] => {
    return blocks.map(block => {
      if (block.id === parentId && block.children) {
        return {
          ...block,
          children: insertAt(block.children, newIndex)
        };
      }
      if (block.children) {
        return {
          ...block,
          children: updateParent(block.children)
        };
      }
      return block;
    });
  };

  if (parentId) {
    // Move within a parent block's children
    return {
      ...design,
      blocks: updateParent(designWithoutBlock.blocks)
    };
  } else {
    // Move at root level
    return {
      ...design,
      blocks: insertAt(designWithoutBlock.blocks, newIndex)
    };
  }
}
